import {
  createStyles,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from "@material-ui/core";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";

import Chart from "./Chart/Chart";
import { formatKeyName, replaceCommon } from "../../../../../utils/ReplaceIcon";
import {
  getLocalStore,
  setLocalStore,
} from "../../../../../utils/store/localStore";
import { CustomTheme } from "../../../../../types/customTheme";

export interface DashboardProps {
  currentStartDate: any;
  currentEndDate: any;
  shortCurrentDateRange: any;
  previousDashboardNodeList: any;
  dashboardNodeList: any;
  isEmpty?: any;
  order: any;
  checkedCurrentPreviousToggle: any;
  value: any;
  setValue: any;
  isChannel?: any;
  isAverage?: any;
  currentSalesTotal?: any;
  previousSalesTotal?: any;
  salesTotalNode?: any;
}

/*
  DashboardChart Component:
  This component renders a dashboard chart with various chart types (line, area, stacked area, and stacked bar).
  It provides UI controls for selecting different chart types, toggling between current and previous periods,
  and displays values for the current and previous periods.

*/
const DashboardChart: React.FunctionComponent<DashboardProps> = ({
  currentStartDate,
  currentEndDate,
  shortCurrentDateRange,
  previousDashboardNodeList,
  dashboardNodeList,
  isEmpty,
  order,
  checkedCurrentPreviousToggle,
  value,
  setValue,
  isChannel,
  isAverage,
  currentSalesTotal,
  previousSalesTotal,
  salesTotalNode,
}) => {
  const [dateDifference, setDateDifference] = useState(0);
  const [data, setData] = useState<any>({ datasets: [] });
  const [dataSetLength, setDataSetLength] = useState(0);

  /* set the data object format. 
      ex:- data = [97, 79, 73, 60, 59]
      currentDataConvertObject = {0: 97, 1: 79, 2: 73, 3: 60, 4: 59}*/
  const handleConvertObjectFormat = (dataConvertObject: any, data: any) => {
    data.map((data2: any, index1: any) => {
      if (dataConvertObject[index1] === undefined) {
        dataConvertObject[index1] = 0;
      }
      dataConvertObject[index1] = dataConvertObject[index1] + data2;
    });
    return dataConvertObject;
  };

  /* Get the key of mergePreviousAndCurrentDashboardNodeList. 
     This will get the desired values ​​for the tooltip of the previous and current charts.
     ex:- mergePreviousAndCurrentDashboardNodeList = 
              {2022-11-08: {total_amount: 59}, 2022-11-09: {total_amount: 64}, 2022-11-10: {total_amount: 7}}
          output = ['total_amount']  */
  const handleTooltipKey = (mergePreviousAndCurrentDashboardNodeList: any) => {
    let dashboardKey: any = [];
    if (!_.isEmpty(mergePreviousAndCurrentDashboardNodeList)) {
      const dashboardValue: any = Object.values(
        mergePreviousAndCurrentDashboardNodeList,
      );

      if (!_.isEmpty(dashboardValue)) {
        dashboardValue.map((value: any, key: any) => {
          Object.keys(value).map((data: any) => {
            const selectedList: any = new Set([...dashboardKey, data]);

            dashboardKey = [...selectedList];
          });
        });
      }
    }
    return dashboardKey;
  };

  /* Entering the value corresponding to the relevant date into the object. Using a previous date range.
     Here values ​​related to dates are obtained.
     Here, the previous day's list data length, 
     the sum of the list values, the list data as a list [{total_amount: [66, 9, 73, 14, 14]}], 
     and the list data as an object are returned {total_amount: [66, 9, 73, 14, 14]}. */

  /* Entering the value corresponding to the relevant date into the object. Using a current date range.
      Here values ​​related to dates are obtained.
      Here, the current day's list data length, 
      the sum of the list values, the list data as a list [{total_amount: [66, 9, 73, 14, 14]}], 
      and the list data as an object are returned {total_amount: [66, 9, 73, 14, 14]}. */
  const handleCurrentChartData = (dashboardKey: any, data1: any) => {
    let currentDataList: any = [];
    let currentDataTotal = 0;
    let currentDataObject: any = {};
    dashboardKey.map((data: any, index: number) => {
      let currentDateList: any = [];
      // Create a new Date object
      let dateObj = new Date();
      // Set the hour to 22
      dateObj.setUTCHours(0);

      shortCurrentDateRange.map((date: any, index: number) => {
        if (data !== "wix") {
          if (!_.isEmpty(data1[date])) {
            if (data1[date][data] !== undefined) {
              currentDateList.push(data1[date][data]);
              currentDataTotal = currentDataTotal + data1[date][data];
            } else {
              currentDateList.push(0);
            }
          } else {
            currentDateList.push(0);
          }
        }
      });

      currentDataList.push({ [`${data}C`]: currentDateList });
      currentDataObject[`${data}C`] = currentDateList;
    });

    return {
      currentDataTotal,
      currentDataList,
      currentDataObject,
    };
  };

  function generateRandomDataNumber(startDate: any, endDate: any) {
    const out: any = {};
    const start = startDate;
    const end = endDate;

    // Loop from start to end date (inclusive)
    for (let d = start; d <= end; d = d + 1) {
      const dateKey = d;

      out[dateKey] = {
        totalRewardCost: Math.floor(Math.random() * 50), // 0–49
        totalRewardsRedeemed: Math.floor(Math.random() * 30), // 0–29
        totalSpend: Math.floor(Math.random() * 100), // 0–99
        totalStampsEarned: Math.floor(Math.random() * 10), // 0–9
        totalStampsExpired: Math.floor(Math.random() * 5), // 0–4
      };
    }

    return out;
  }

  function generateRandomData(startDate: any, endDate: any) {
    const out: any = {};
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Loop from start to end date (inclusive)
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split("T")[0];

      out[dateKey] = {
        totalRewardCost: Math.floor(Math.random() * 50), // 0–49
        totalRewardsRedeemed: Math.floor(Math.random() * 30), // 0–29
        totalSpend: Math.floor(Math.random() * 100), // 0–99
        totalStampsEarned: Math.floor(Math.random() * 10), // 0–9
        totalStampsExpired: Math.floor(Math.random() * 5), // 0–4
      };
    }

    return out;
  }

  useEffect(() => {
    let data = {};

    if (Object.values(dashboardNodeList).length > 0) {
      data = dashboardNodeList;
    } else {
      data = generateRandomData(currentStartDate, currentEndDate);
    }
    const cloneDashboardNodeList = _.cloneDeep(data);
    const clonePreviousDashboardNodeList = _.cloneDeep(
      previousDashboardNodeList,
    );
    /* Merge dashboardNodeList and previousDashboardNodeList. */
    const mergePreviousAndCurrentDashboardNodeList = Object.assign(
      cloneDashboardNodeList,
      clonePreviousDashboardNodeList,
    );

    const dashboardKey = handleTooltipKey(
      mergePreviousAndCurrentDashboardNodeList,
    );

    const { currentDataTotal, currentDataList, currentDataObject } =
      handleCurrentChartData(dashboardKey, data);

    /* Get the date difference between current date range first date and previous date range last date. */

    /* Checking whether the selected date range matches the date range of this month. */
    if (
      shortCurrentDateRange[0] ===
        moment(new Date()).startOf("month").format("YYYY-MM-DD") &&
      shortCurrentDateRange[shortCurrentDateRange.length - 1] ===
        moment(new Date()).endOf("month").format("YYYY-MM-DD")
    ) {
      setDateDifference(dateDifference - 1);
    } else {
      setDateDifference(dateDifference - 1 + shortCurrentDateRange.length);
    }

    let dataSet: any = [];
    let dataSetStackedAreaGraphCurrent: any = [];
    let dataSetStackedBarGraph: any = [];
    let dataSetStackedAreaGraphPrevious: any = [];
    let currentDataConvertObject: any = {};

    const handleChannelBorderColor = (channelName: string) => {
      return "#3A9D76";
    };

    /* For ChartJs, processing the data of the current chart and setting the current data in the object format. */
    currentDataList.map((currentDataObject: any, index: number) => {
      //Get the key of the current data object
      const currentDataKey = Object.keys(currentDataObject).toString();
      // Remove last character it.
      const removeLastCharacter = currentDataKey.slice(0, -1);
      // Replace _ with a space.
      const key = replaceCommon(removeLastCharacter, "_", " ");

      if (key !== "wix") {
        Object.values(currentDataObject).map((data: any) => {
          const text = key.split(" ");

          // Capitalize first letter in the more than word.
          let capitalizeFirstLetterInMoreThanWord = "";
          text.map((word: string) => {
            capitalizeFirstLetterInMoreThanWord =
              capitalizeFirstLetterInMoreThanWord + " " + formatKeyName(word);
          });

          // Set the chartJs current data
          dataSet.push({
            label: "Current Period",
            data: data,
            borderColor: handleChannelBorderColor(key),
            backgroundColor: handleChannelBorderColor(key),
            hoverBackgroundColor: handleChannelBorderColor(key),
            tension: 0,
            cubicInterpolationMode: "linear",
            fill: false,
            borderWidth: 2,
            key: capitalizeFirstLetterInMoreThanWord,
          });

          dataSetStackedAreaGraphCurrent.push({
            label: "Current Period",
            data: data,
            borderColor: handleChannelBorderColor(key),
            backgroundColor: handleChannelBorderColor(key),
            hoverBackgroundColor: handleChannelBorderColor(key),
            tension: 0,
            cubicInterpolationMode: "linear",
            fill: true,
            borderWidth: 1,
            key: capitalizeFirstLetterInMoreThanWord,
          });

          dataSetStackedBarGraph.push({
            label: "Current Period",
            data: data,
            borderColor: handleChannelBorderColor(key),
            backgroundColor: handleChannelBorderColor(key),
            hoverBackgroundColor: handleChannelBorderColor(key),
            tension: 0,
            cubicInterpolationMode: "linear",

            fill: true,
            borderWidth: 1,
            key: capitalizeFirstLetterInMoreThanWord,
            stack: "Stack 1",
          });

          currentDataConvertObject = handleConvertObjectFormat(
            currentDataConvertObject,
            data,
          );
        });
      }
    });
    /* For ChartJs, processing the data of the previous chart and setting the previous data in the object format. */

    /* Merge current object and previous object. */
    const merge = {
      ...currentDataObject,
    };

    /* Calculate the percentage of the current and previous data */
    dashboardKey.map((data: any, index: any) => {
      let current = merge[`${data}C`];
      let previous = merge[`${data}P`];
    });

    // Convert percentage list to object.

    setDataSetLength(dataSet.length / 2);

    const totalSpend = dataSet.find((d: any) => d.key.trim() === "Total Spend");
    const totalRewardCost = dataSet.find(
      (d: any) => d.key.trim() === "Total Reward Cost",
    );
    const outputData = totalSpend.data.map((spend: any, i: any) => {
      const reward = totalRewardCost.data[i] || 0;
      const value = spend !== 0 ? (reward / spend) * 100 : 0;
      // Show 1 decimal if needed, otherwise integer
      return value % 1 === 0 ? value : parseFloat(value.toFixed(1));
    });

    const output = [
      {
        ...totalSpend,
        data: outputData,
        key: " Cost-to-Spend Ratio",
        label: "Current Period",
      },
    ];
    dataSet.push(output[0]);
    setData({
      labels: shortCurrentDateRange,
      datasets: dataSet,
    });
  }, [
    previousDashboardNodeList,
    dashboardNodeList,
    shortCurrentDateRange,
    previousSalesTotal,
    currentSalesTotal,
    getLocalStore("isDarkMode"),
    value,
  ]);
  useEffect(() => {
    if (getLocalStore("chartType")) {
      setValue(getLocalStore("chartType"));
    } else {
      setLocalStore("chartType", "line");
      setValue("line");
    }
  }, []);

  const calculateTotal = (data: any) => {
    const total = data.data.reduce((sum: any, num: any) => sum + num, 0);
    const key = data.key.trim();
    const rewardCost = salesTotalNode?.[0]?.["totalRewardCost"] || 0;
    const spend = salesTotalNode?.[0]?.["totalSpend"] || 0;
    const ratio = spend !== 0 ? (rewardCost / spend) * 100 : 0;
    const output =
      key === "Total Spend" || key === "Total Reward Cost"
        ? total.toFixed(2)
        : key === "Cost-to-Spend Ratio"
        ? `${ratio % 1 === 0 ? ratio : parseFloat(ratio.toFixed(1))}%`
        : total.toFixed(0);

    return output;
  };

  const orders = [
    "Total Stamps Earned",
    "Total Rewards Redeemed",
    "Total Stamps Expired",
    "Total Spend",
    "Total Reward Cost",
    "Cost-to-Spend Ratio",
  ];

  const orderedInput = data?.datasets
    ? orders?.map((orderKey) =>
        data?.datasets?.find((item: any) => item?.key?.trim() === orderKey),
      )
    : [];
  return (
    <>
      <Grid container spacing={2}>
        {value === "line" &&
          orderedInput[0] !== undefined &&
          orderedInput?.map((dataset: any, i: any) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              // style={{
              //   marginTop: i === 0 || i === 1 ? "16px" : "8px",
              //   paddingLeft: i % 2 === 0 ? "0px" : "8px",
              //   paddingRight: i % 2 === 0 ? "8px" : "0px",
              //   marginBottom: i === 4 ? "16px" : "8px",
              // }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <Typography
                    variant="caption"
                    style={{ fontWeight: "bold" }}
                    align="left"
                  >
                    {dataset.key === " Total Spend"
                      ? "Total Customer Spend"
                      : dataset.key}
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{ fontWeight: "bold" }}
                    align="left"
                  >
                    {calculateTotal(dataset)}
                  </Typography>
                </div>
              </div>
              <Chart
                data={{ datasets: [dataset], labels: data.labels }}
                dateDifference={dateDifference}
                dataSetLength={dataSetLength}
                isEmpty={isEmpty}
                currentStartDate={currentStartDate}
                currentEndDate={currentEndDate}
                order={order}
                isChannel={isChannel}
                value="line"
              />
            </Grid>
          ))}
      </Grid>
    </>
  );
};

export default DashboardChart;
